import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "cm-align-center gap-x-5" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "cm-align-center gap-x-5" }
const _hoisted_4 = { class: "cm-align-center gap-x-5" }

import { getSvgIconDataUrl } from '@/utils/svg-icon-data-url'

import { reactive, ref, computed, PropType, nextTick } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import {
  DeviceStatusQueryParams,
  DeviceStatusService,
  DeviceStatus,
} from '@/views/dashboard/safe-status/device-status/device-status.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  ActionType,
  FormSize,
  FormType,
  ObjectFormItem,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import { formatMapToOptions } from '@/utils/tool'
import { login_status } from '@/views/dashboard/manage/terminal-traffic-control/traffic-control/types'
import { getLoginStatusLabelMap } from '@/views/dashboard/manage/terminal-traffic-control/traffic-control/traffic-control-i18n-labels'
import { ServicePermissionService, PermissionFe } from '@/services/service-permission.service'

export type TrafficStatusEmbedDefaults = {
  key_word?: string
  start_time?: string
  end_time?: string
} | null

type ExcludeRequestParams = Extract<keyof DeviceStatusQueryParams, 'login_status' | 'asc' | 'group_id'>

export default /*#__PURE__*/_defineComponent({
  __name: 'traffic-status-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  /** 从排行图等打开时带入关键词（如计算机名）与时间范围（秒级字符串） */
  embedDefaults: {
    type: Object as PropType<TrafficStatusEmbedDefaults>,
    default: null
  },
},
  emits: ['viewAll', 'detail'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()

const loginStatusLabelMap = computed(() => {
  void locale.value
  return getLoginStatusLabelMap(t)
})

const props = __props

const emit = __emit

const injector = useReflectiveInjector([DeviceStatusService])
const deviceStatusService = injector.get(DeviceStatusService)
const servicePermissionService = injector.get(ServicePermissionService)
const dataApi = deviceStatusService.getDeviceStatusList.bind(deviceStatusService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.flow_monitor)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

const queryParams = reactive<Partial<DeviceStatusQueryParams>>({
  group_id: -1,
  sort: 'login_status',
  asc: true,
  key_word: '',
  login_status: null,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  start_time: undefined,
  end_time: undefined,
})

function applyEmbedDefaults () {
  const ed = props.embedDefaults
  if (!ed) {
    queryParams.start_time = undefined
    queryParams.end_time = undefined
    return
  }
  if (ed.key_word !== undefined && ed.key_word !== null) {
    queryParams.key_word = ed.key_word
  }
  queryParams.start_time = ed.start_time
  queryParams.end_time = ed.end_time
}

/** 原在 onMounted：须在 CmTable 首次 getList 前写入，避免首包无 embed 再 refresh 双请求 */
applyEmbedDefaults()

const filterFormColumns = computed<ObjectFormItem<DeviceStatusQueryParams, ExcludeRequestParams>>(() => {
  void locale.value
  return {
    group_id: {
      hidden: () => false,
      colOpt:{ style:'flex:0 0 150px' },
      slot: true,
      label: t('portManage.group'),
      outSearchFiledFlag: true,
      hiddenAllLabel: true,
      type: FormType.Select,
      events: {
        change: handleQuery,
      },
    },
    login_status: {
      colOpt: { span: 12 },
      label: t('portManage.onlineStatus'),
      hiddenAllLabel: true,
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      dictData: formatMapToOptions(loginStatusLabelMap.value, true),
      outSearchFiledFlag: true,
      events:{
        change: handleQuery
      }
    }
  }
})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('portManage.operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (rowItem: DeviceStatus) => [
      {
        name: t('portManage.detail'),
        type: 'detail',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : 'background: transparent;padding:0',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    path: 'list',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'device_id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      ...computerInfoCols(false, true, t),
      {
        label: t('portManage.onlineStatus'),
        prop: 'login_status',minWidth:150
      },
      {
        label: t('portManage.uploadSpeed'),
        prop: 'up_speed',minWidth:180
      },
      {
        label: t('portManage.downloadSpeed'),
        prop: 'down_speed',minWidth:180
      },
      {
        label: t('portManage.todayUpload'),
        prop: 'up_flow',minWidth:180
      },
      {
        label: t('portManage.todayDownload'),
        prop: 'down_flow',minWidth:180
      },
    ],
  }
})

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  queryParams.login_status = null
  queryParams.group_id = -1
  if (!props.embedDefaults) {
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  } else {
    applyEmbedDefaults()
  }
  void refreshList()
}

function viewAll() {
  if (!hasPermission) return
  emit('viewAll')
}

async function updateGroupId(groupId: number) {
  // GroupTreeSelector 初始化会回传当前分组；未变化时不重复请求
  if (queryParams.group_id === groupId) return
  queryParams.group_id = groupId
  queryParams.page = 1
  await refreshList()
}

function handleAction({ type, rowItem }: { type: any; rowItem?: DeviceStatus }) {
  if (type === 'detail' && rowItem) {
    emit('detail', rowItem)
  }
}

function download(type: 0 | 1) {
  const params: Partial<DeviceStatusQueryParams> = {
    group_id: queryParams.group_id,
    key_word: queryParams.key_word,
    login_status: queryParams.login_status,
    sort: queryParams.sort,
    asc: queryParams.asc,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
  }
  deviceStatusService.download(type, params, t('portManage.exportTrafficStatus'))
}

__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_CmSvgIcon = _resolveComponent("CmSvgIcon")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    tableOpt: tableOpt.value,
    tableActOpt: tableActOpt.value,
    "has-permission": _unref(hasPermission),
    api: _unref(dataApi),
    modelValue: queryParams,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
    pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
    onHandleAction: handleAction
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: queryParams,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
        "form-columns": filterFormColumns.value,
        disabled: !_unref(hasPermission),
        "show-search": true,
        "search-placeholder": _ctx.$t('remoteLoginLog.searchPlaceholder'),
        "popover-threshold": 2,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleReset
      }, {
        group_id: _withCtx(() => [
          _createVNode(GroupTreeSelector, {
            width: "100%",
            "terminal-type": [1, 3],
            onSelected: updateGroupId
          })
        ]),
        actions: _withCtx(() => [
          _createVNode(ExportDropdown, {
            "has-permission": _unref(hasPermission),
            onExport: download
          }, null, 8, ["has-permission"]),
          _createVNode(_component_el_button, {
            type: "primary",
            size: "small",
            plain: "",
            disabled: !_unref(hasPermission),
            onClick: viewAll
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('terminalTrafficStateList.viewAll')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
    ]),
    login_status: _withCtx((slotProps) => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("img", {
          src: (slotProps as any).row.login_status === _unref(login_status).online ? _unref(getSvgIconDataUrl)('views/dashboard/safe-status/device-status/_images/5') : _unref(getSvgIconDataUrl)('views/dashboard/safe-status/device-status/_images/4'),
          alt: "",
          style: {"width":"16px","height":"16px"}
        }, null, 8, _hoisted_2),
        _createElementVNode("span", null, _toDisplayString(loginStatusLabelMap.value[(slotProps as any).row.login_status] || '--'), 1)
      ])
    ]),
    up_flow: _withCtx((slotProps) => [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_CmSvgIcon, {
          name: "views/dashboard/safe-status/device-status/_images/7",
          monochrome: "",
          size: 16
        }),
        _createElementVNode("span", null, _toDisplayString((slotProps as any).row.up_flow || '--'), 1)
      ])
    ]),
    down_flow: _withCtx((slotProps) => [
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_component_CmSvgIcon, {
          name: "views/dashboard/safe-status/device-status/_images/6",
          monochrome: "",
          size: 16
        }),
        _createElementVNode("span", null, _toDisplayString((slotProps as any).row.down_flow || '--'), 1)
      ])
    ]),
    _: 1
  }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]))
}
}

})